import {
  useDrag,
  useDrop,
  DragObjectWithType,
  DropTargetMonitor,
  DragElementWrapper,
  DragSourceOptions
} from 'react-dnd';
import { DRAG_DROP_TYPES } from 'types';
import { useCallback } from 'react';
import omit from 'lodash/omit';
import { isEqual } from 'lodash';

interface DragOpts {
  type: string;
  col: number;
  item: number;
  itemId: number;
}
type DragDropOpts = {
  endDrag: {
    dropEffect: any;
    canSwap: boolean;
    items: {
      originIndices: { col: number; item: number };
      targetIndices: { col: number; item: number };
    };
  };
  didDrop: boolean;
  canDrop: boolean;
  isOver: boolean;
  type: any;
  isDragging: boolean;
};
type Events = {
  drop: (ref: any) => any;
  drag: DragElementWrapper<DragSourceOptions>;
};
export type DragIndices = {
  col: number;
  item: number;
  itemId: number;
};

type Inputs = {
  ref: any;
  dragIndices: DragIndices;
  draggable?: boolean;
};
const useDragDrop = ({ ref, dragIndices, draggable }: Inputs): [DragDropOpts, Events] => {
  const { col, item: dragItem } = dragIndices;
  const [dragOpts, _drag] = useDrag<DragOpts, DragObjectWithType, { isDragging: boolean }>({
    item: { type: DRAG_DROP_TYPES.PAPER_CARD, ...dragIndices },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  const [dropOpts, _drop]: any = useDrop({
    accept: DRAG_DROP_TYPES.PAPER_CARD,
    collect: (monitor: DropTargetMonitor) => ({
      endDrag: !draggable ? false : monitor.getDropResult() || {},
      didDrop: !draggable ? false : monitor.didDrop(),
      canDrop: !draggable ? false : monitor.canDrop(),
      isOver: !draggable ? false : monitor.isOver(),
      type: monitor.getItemType()
    }),
    drop(item: any) {
      const originIndices = omit(item, ['type', 'itemId']);
      const targetIndices = { col, item: dragItem };
      const isSameElement = isEqual(originIndices, targetIndices);

      if (!ref?.current) return undefined;
      if (isSameElement) return undefined;

      item.index = targetIndices;
      return { canSwap: true, items: { originIndices, targetIndices } };
    }
  });

  const drag = useCallback(_drag, [_drop]);
  const drop = useCallback(_drop, [ref]);

  return [
    { ...dragOpts, ...dropOpts },
    { drag, drop }
  ];
};

export default useDragDrop;
